import { LogOut, Menu, Palette, ChevronDown } from 'lucide-react';
import { useState, useRef, useEffect, type ReactNode, type ComponentType } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { useTheme } from '@elka/theme';

const elkaLogo = '/logo/elka-logo.png';

export interface DesktopHeaderNavSubItem {
  to: string;
  icon: ComponentType<{ size?: number; className?: string; strokeWidth?: number }>;
  label: string;
}

export interface DesktopHeaderNavItem {
  to: string;
  icon: ComponentType<{ size?: number; className?: string; strokeWidth?: number }>;
  label: string;
  color: string;
  end?: boolean;
  subItems?: DesktopHeaderNavSubItem[];
}

export interface DesktopHeaderUser {
  displayName: string | null;
  photoURL: string | null;
}

export interface DesktopHeaderProps {
  title: string;
  mobileTitle: string;
  subtitle: string;
  logoHref: string;
  navItems: DesktopHeaderNavItem[];
  user?: DesktopHeaderUser | null;
  onLogout?: () => void;
  /** Extra elements rendered between theme toggle and user menu (e.g. NotificationDropdown) */
  toolbarExtras?: ReactNode;
  /** Extra elements rendered before nav tabs (e.g. SectionSwitcher + divider) */
  navPrefix?: ReactNode;
  /** Mobile menu button click handler -- header renders the hamburger button internally */
  onMobileMenuOpen?: () => void;
}

export function DesktopHeader({
  title,
  mobileTitle,
  subtitle,
  logoHref,
  navItems,
  user,
  onLogout,
  toolbarExtras,
  navPrefix,
  onMobileMenuOpen,
}: DesktopHeaderProps) {
  const { theme, colors, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [openNavDropdown, setOpenNavDropdown] = useState<string | null>(null);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const navDropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (userMenuRef.current && !userMenuRef.current.contains(target)) {
        setIsUserMenuOpen(false);
      }
      if (navDropdownRef.current && !navDropdownRef.current.contains(target)) {
        setOpenNavDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogoutClick = () => {
    setIsUserMenuOpen(false);
    onLogout?.();
  };

  // Get display name: use first part of displayName or fallback to empty string
  const displayName = user?.displayName?.split(' ')[0] || '';
  const userInitial = displayName.charAt(0).toUpperCase();

  return (
    <header
      className={`relative bg-gradient-to-r ${colors.headerBg} sticky top-0 z-50 shadow-2xl`}
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      {/* Animated background effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className={`absolute -top-24 -right-24 h-96 w-96 ${colors.headerGlow1} animate-pulse rounded-full opacity-30 mix-blend-multiply blur-3xl filter`}
        ></div>
        <div
          className={`absolute -bottom-24 -left-24 h-96 w-96 ${colors.headerGlow2} animate-pulse rounded-full opacity-30 mix-blend-multiply blur-3xl filter`}
          style={{ animationDelay: '1s' }}
        ></div>
        <div
          className={`absolute top-1/2 left-1/2 h-96 w-96 -translate-x-1/2 -translate-y-1/2 transform ${colors.headerGlow3} animate-pulse rounded-full opacity-20 mix-blend-multiply blur-3xl filter`}
          style={{ animationDelay: '2s' }}
        ></div>
      </div>

      <div className="relative max-w-full px-4 md:px-6">
        <div className="flex h-16 items-center justify-between border-b border-white/10 md:h-20">
          {/* Mobile Menu Button */}
          {onMobileMenuOpen && (
            <button
              onClick={onMobileMenuOpen}
              className="rounded-lg p-2 text-white transition-colors hover:bg-white/10 md:hidden"
              aria-label="פתח תפריט"
            >
              <Menu size={24} />
            </button>
          )}

          {/* Logo and Title */}
          <div
            className="flex flex-1 cursor-pointer items-center gap-2 md:flex-initial md:gap-4"
            onClick={() => navigate(logoHref)}
          >
            <div className="group relative">
              <div
                className={`absolute inset-0 bg-gradient-to-r ${colors.primaryGradient} animate-pulse rounded-2xl opacity-75 blur transition duration-300 group-hover:opacity-100`}
              ></div>
              <div
                className={`relative flex h-10 w-10 transform items-center justify-center rounded-2xl bg-white p-2 shadow-2xl transition duration-300 group-hover:scale-110 md:h-14 md:w-14`}
              >
                <img src={elkaLogo} alt="Elka Logo" className="h-full w-full object-contain" />
              </div>
            </div>
            <div className="sm:hidden">
              <h1
                className={clsx(
                  'text-lg leading-tight font-black text-balance',
                  theme === 'arabGolden'
                    ? 'text-white/90 drop-shadow-sm'
                    : `bg-gradient-to-r bg-clip-text text-transparent ${colors.titleGradient}`
                )}
              >
                {mobileTitle}
              </h1>
            </div>
            <div className="hidden sm:block">
              <div className="flex items-center gap-2">
                <h1
                  className={`bg-gradient-to-r bg-clip-text text-xl font-black text-transparent md:text-3xl ${colors.titleGradient} tracking-tight text-balance transition-opacity hover:opacity-80`}
                >
                  {title}
                </h1>
              </div>
              <p className={`text-xs md:text-sm ${colors.subtitleText} mt-0.5 font-medium`}>
                {subtitle}
              </p>
            </div>
          </div>

          {/* Theme Toggle & User Info */}
          <div className="flex items-center gap-2 md:gap-4">
            {/* Theme Toggle Button - desktop only */}
            <button
              onClick={toggleTheme}
              className="group relative hidden overflow-hidden rounded-xl px-4 py-2.5 font-semibold text-white transition-transform hover:scale-105 active:scale-[0.96] md:block"
              title={theme === 'elkaBlue' ? 'עבור לסגנון ערבי זהוב' : 'עבור לסגנון כחול'}
            >
              <div
                className={`absolute inset-0 ${theme === 'elkaBlue' ? 'bg-gradient-to-r from-amber-500 via-yellow-500 to-orange-500' : 'bg-gradient-to-r from-[#0C1A55] via-[#3C5FA8] to-[#0C1A55]'} transition-transform group-hover:scale-110`}
              ></div>
              <div className="relative flex items-center gap-2">
                <Palette size={18} />
                <span className="text-sm">{theme === 'elkaBlue' ? 'זהב' : 'כחול'}</span>
              </div>
            </button>

            {/* Toolbar Extras (e.g. NotificationDropdown) */}
            {toolbarExtras}

            {/* User Info and Logout (menu) - desktop only */}
            {user && (
              <div className="relative hidden md:block" ref={userMenuRef}>
                <button
                  type="button"
                  onClick={() => setIsUserMenuOpen((prev) => !prev)}
                  className="group relative flex items-center gap-3 rounded-2xl border border-white/20 px-4 py-2.5 text-white backdrop-blur-xl transition hover:border-white/40"
                >
                  <div className="flex items-center gap-3">
                    {user.photoURL ? (
                      <img
                        src={user.photoURL}
                        alt={displayName}
                        referrerPolicy="no-referrer"
                        className="h-8 w-8 rounded-full border-2 border-white/30 object-cover shadow-lg"
                      />
                    ) : (
                      <div
                        className={`h-8 w-8 rounded-full bg-gradient-to-br ${colors.primaryGradient} flex items-center justify-center text-base font-bold text-white shadow-lg`}
                      >
                        {userInitial}
                      </div>
                    )}
                    <div className="flex flex-col items-start">
                      <p className="text-[11px] text-gray-300">שלום,</p>
                      <p className="text-sm font-semibold">{displayName}</p>
                    </div>
                  </div>
                </button>

                {isUserMenuOpen && onLogout && (
                  <div className="absolute left-0 z-50 mt-2 w-44 overflow-hidden rounded-2xl bg-white text-slate-900 shadow-[0_1px_2px_rgba(0,0,0,0.06),0_12px_32px_rgba(0,0,0,0.14)]">
                    <button
                      onClick={handleLogoutClick}
                      className="flex w-full items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50"
                    >
                      <LogOut size={18} className="shrink-0" />
                      התנתק
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Navigation - Desktop Only */}
        <nav ref={navDropdownRef} className="hidden items-center gap-3 py-4 md:flex">
          {/* Nav Prefix (e.g. SectionSwitcher + divider) */}
          {navPrefix}

          {/* Navigation Tabs */}
          {navItems &&
            navItems.map((item) => {
              const hasSubItems = item.subItems && item.subItems.length > 0;
              const isGroupActive = hasSubItems
                ? item.subItems!.some((sub) => location.pathname === sub.to)
                : false;

              if (hasSubItems) {
                const isOpen = openNavDropdown === item.label;
                return (
                  <div key={item.label} className="relative">
                    <button
                      type="button"
                      onClick={() => setOpenNavDropdown(isOpen ? null : item.label)}
                      className={clsx(
                        'group relative flex items-center gap-2.5 overflow-hidden rounded-xl px-5 py-2.5 text-sm font-semibold transition-transform',
                        isGroupActive || isOpen ? 'scale-105' : 'hover:scale-105'
                      )}
                    >
                      <div
                        className={clsx(
                          'absolute inset-0 bg-gradient-to-r transition-opacity',
                          item.color,
                          isGroupActive || isOpen
                            ? 'opacity-100'
                            : 'opacity-0 group-hover:opacity-90'
                        )}
                      />
                      {isGroupActive && (
                        <div
                          className={`absolute inset-0 bg-gradient-to-r ${item.color} opacity-50 blur-xl`}
                        />
                      )}
                      <div
                        className={clsx(
                          'absolute inset-0 rounded-xl border-2 transition-colors',
                          isGroupActive || isOpen
                            ? 'border-white/40'
                            : 'border-white/10 group-hover:border-white/20'
                        )}
                      />
                      <div className="relative flex items-center gap-2.5">
                        <item.icon
                          size={20}
                          className={clsx(
                            'transition-colors',
                            isGroupActive || isOpen
                              ? 'text-white'
                              : 'text-blue-200 group-hover:text-white'
                          )}
                          strokeWidth={2.5}
                        />
                        <span
                          className={clsx(
                            'transition-colors',
                            isGroupActive || isOpen
                              ? 'font-bold text-white'
                              : 'text-blue-200 group-hover:text-white'
                          )}
                        >
                          {item.label}
                        </span>
                        <ChevronDown
                          size={16}
                          className={clsx(
                            'transition-transform',
                            isOpen && 'rotate-180',
                            isGroupActive || isOpen
                              ? 'text-white'
                              : 'text-blue-200 group-hover:text-white'
                          )}
                        />
                      </div>
                    </button>
                    {isOpen && (
                      <div className="absolute top-full left-0 z-50 mt-2 min-w-[180px] overflow-hidden rounded-xl bg-white py-1 text-slate-900 shadow-[0_1px_2px_rgba(0,0,0,0.06),0_12px_32px_rgba(0,0,0,0.14)]">
                        {item.subItems!.map((sub) => {
                          const isSubActive = location.pathname === sub.to;
                          const SubIcon = sub.icon;
                          return (
                            <NavLink
                              key={sub.to}
                              to={sub.to}
                              onClick={() => setOpenNavDropdown(null)}
                              className={clsx(
                                'flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors',
                                isSubActive
                                  ? 'bg-slate-100 font-semibold text-[#0C1A55]'
                                  : 'text-slate-600 hover:bg-slate-50'
                              )}
                            >
                              <SubIcon
                                size={18}
                                className={isSubActive ? 'text-[#3C5FA8]' : 'text-slate-400'}
                              />
                              {sub.label}
                            </NavLink>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `group relative flex items-center gap-2.5 overflow-hidden rounded-xl px-5 py-2.5 text-sm font-semibold transition-transform ${
                      isActive ? 'scale-105' : 'hover:scale-105'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <div
                        className={`absolute inset-0 bg-gradient-to-r ${item.color} transition-opacity ${
                          isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-90'
                        }`}
                      />
                      {isActive && (
                        <div
                          className={`absolute inset-0 bg-gradient-to-r ${item.color} opacity-50 blur-xl`}
                        />
                      )}
                      <div
                        className={`absolute inset-0 rounded-xl border-2 transition-colors ${
                          isActive
                            ? 'border-white/40'
                            : 'border-white/10 group-hover:border-white/20'
                        }`}
                      />
                      <div className="relative flex items-center gap-2.5">
                        <item.icon
                          size={20}
                          className={`transition-colors ${
                            isActive ? 'text-white' : 'text-blue-200 group-hover:text-white'
                          }`}
                          strokeWidth={2.5}
                        />
                        <span
                          className={`transition-colors ${
                            isActive
                              ? 'font-bold text-white'
                              : 'text-blue-200 group-hover:text-white'
                          }`}
                        >
                          {item.label}
                        </span>
                      </div>
                    </>
                  )}
                </NavLink>
              );
            })}
        </nav>
      </div>
    </header>
  );
}
